import React, { Fragment } from "react";
import moment from "moment";
import { truncate } from "lodash";
import { Tooltip } from "react-tooltip";
import clsx from "clsx";
import millify from "millify";

import SpaceEmptyContent from "./SpaceEmptyContent";
import VendorAvatar from "components/common/VendorAvatar";
import TableBodyItem from "components/common/TableBodyItem";
import { getSalesChannelIconRaw } from "utils/get-sales-channel-icon-raw";
import { calculateStockoutDate } from "utils/calculate-stockoutdate";
import { formatMoney } from "helpers";
import CustomTable from "components/common/CustomTable";
import SearchTextField from "components/common/InputField/SearchTextField";
import useGetProductList from "hooks/inventoryHooks/useGetProductList";
import Dropdown from "components/common/Dropdown";
import useGetSalesChannel from "hooks/Dashboard/useGetSalesChannel";
import MenuItem from "components/common/MenuItem";
import Checkbox from "components/common/Checkbox";
import { getSalesChannelIcon } from "utils/get-sales-channel-icon";
import { normalizeSalesChannelName } from "utils/normalize-sales-channel-name";
import Loader from "components/common/Loader";

export default function SpaceDetailsPinnedSkuTab() {
    const {
        filteredData: data,
        handleDebouncedChange,
        handleSearch,
        isFetching: productListLoading,
        search,
        pagination,
        channel,
        updatedPage,
        handleChannelChange,
        handleClearChannel,
        setForecastNeeds,
        forecastNeeds,
        setUpdatedPage,
    } = useGetProductList(
        global.document.getElementById("table-container-one"),
        "Finished goods",
        true,
        true,
        undefined,
        undefined,
        "starred"
    );

    const { data: salesChannel, isFetching: salesChannelLoading } = useGetSalesChannel();

    const tableHeader = [
        {
            title: "Product name",
            widthClass: "w-[250px]",
        },
        {
            title: "Categories",
            widthClass: "w-[100px]",
            tooltipIcon: "categories",
            tooltipContent:
                "Groups or classifications that organize products such as Finished goods, Components, and Work-in-Progress (WIP), helping you manage your inventory efficiently.",
        },
        {
            title: "Channel",
            widthClass: "w-[100px]",
            // widthClass: "w-[120px]",
            // tooltipIcon: "channel",
            // tooltipContent:
            //     "Groups or classifications that organize products such as Finished goods, Components, and Work-in-Progress (WIP), helping you manage your inventory efficiently.",
        },
        {
            title: "Current stock level",
            widthClass: "w-[100px]",
            tooltipIcon: "current-stock-level",
            tooltipContent:
                "The actual quantity of the product currently available in stock for sale or use at this moment.",
        },
        {
            title: "Forecasted needs",
            widthClass: "w-[100px]",
            tooltipIcon: "forecasted-sales",
            tooltipContent:
                "The estimated amount of the product required to meet anticipated demand over your selected forecast window (between 1 and 6 months)",
        },
        {
            title: "On order",
            widthClass: "w-[100px]",
            tooltipIcon: "on-order",
            tooltipContent:
                "The quantity of this product that has been ordered from suppliers but has not yet been received (incoming inventory)",
        },
        {
            title: "Re-order before",
            widthClass: "w-[100px]",
            tooltipIcon: "stockout-date",
            tooltipContent:
                "The latest date you should place a purchase order to avoid stockouts, based on lead time and forecasted needs. If the required date is within 7 days, we show “Immediately” & “the today's date” to signal urgency.",
        },
        {
            title: "Forecasted stockout date",
            widthClass: "w-[100px]",
            tooltipIcon: "forecasted-stockout-date",
            tooltipContent:
                "This is the projected date when this product is expected to run out of stock based on current inventory levels and demand trends. Use it to stay ahead with timely reorders.",
        },
    ];

    const tableBody = data?.map((product, idx) => {
        const givenDate = moment(product?.forcastData?.stockoutDate);
        const today = moment().startOf("day");
        let differenceInDays = 0;

        if (givenDate.isValid()) {
            differenceInDays = givenDate.diff(today, "days");
        }

        const isPinned = product?.isPinned;

        return [
            {
                content: (
                    <TableBodyItem>
                        <div className="flex items-center space-x-3">
                            <i
                                className={`ri-star-fill text-lg ${
                                    isPinned ? "text-[#FCD34D]" : "text-[#E2E8F0]"
                                }`}
                            ></i>
                            {product?.productImageDetails?.productAvatar ||
                            product?.productImageDetails?.productImages?.[0] ? (
                                <VendorAvatar
                                    imageSrc={
                                        product?.productImageDetails?.productAvatar ||
                                        product?.productImageDetails?.productImages?.[0]
                                    }
                                    size={32}
                                    name={product?.productName}
                                    containerClassname="!rounded-md"
                                />
                            ) : (
                                <div className="w-8 h-8 rounded shrink-0 flex items-center justify-center bg-slate-200 ">
                                    <i className="ri-box-2-fill text-lg text-slate-500"></i>
                                </div>
                            )}
                            <div>
                                <div>
                                    <a id={`product-column-${idx}`}>
                                        <span className="block text-sm">
                                            {truncate(product?.productName, {
                                                length: 30,
                                            })}
                                        </span>
                                        <span className="text-slate-500 text-sm">
                                            {product?.sku ? `SKU-${product?.sku}` : ""}
                                        </span>
                                    </a>
                                    <Tooltip
                                        anchorSelect={`#product-column-${idx}`}
                                        place={"right"}
                                        content={product?.productName}
                                        style={{
                                            maxWidth: "250px",
                                            zIndex: 9999,
                                            background: "#fff",
                                            borderRadius: "6px",
                                            border: "1px solid #E2E8F0",
                                            color: "#1E293B",
                                            boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.04)",
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </TableBodyItem>
                ),
            },
            {
                content: (
                    <TableBodyItem>
                        <p className="max-w-[100px]">
                            {product?.inventoryCategory || "Not Available"}
                        </p>
                    </TableBodyItem>
                ),
            },
            {
                content: (
                    <TableBodyItem>
                        {product?.channels?.length === 0 ? (
                            "Not available"
                        ) : (
                            <div>
                                <div className="flex items-centers gap-0">
                                    {product?.channels?.map((channel, index) => {
                                        const channelIcon = getSalesChannelIconRaw(channel);
                                        return (
                                            <div>
                                                <Tooltip
                                                    anchorSelect={`#channel-${idx}-${index}`}
                                                    place={"top"}
                                                    content={
                                                        channel?.toLowerCase() === "synth"
                                                            ? "Retail"
                                                            : channel
                                                    }
                                                    style={{
                                                        maxWidth: "250px",
                                                        zIndex: 9999,
                                                        background: "#142837",
                                                        borderRadius: "6px",
                                                        border: "1px solid #142837",
                                                        color: "#fff",
                                                        boxShadow:
                                                            "0px 4px 16px 0px rgba(0, 0, 0, 0.04)",
                                                    }}
                                                />
                                                <div
                                                    id={`channel-${idx}-${index}`}
                                                    className={clsx(
                                                        "rounded-full bg-white w-[26px] h-[26px] flex items-center justify-center border border-slate-200 p-[3px] shadow-lg cursor-pointer"
                                                    )}
                                                    style={{
                                                        ...{
                                                            transform: `translateX(-${
                                                                (index + 1) * 6
                                                            }px)`,
                                                        },
                                                    }}
                                                >
                                                    {typeof channelIcon === "string" ? (
                                                        <img src={channelIcon} alt="" />
                                                    ) : (
                                                        <>{channelIcon}</>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </TableBodyItem>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <TableBodyItem>
                        <span>
                            {product?.forcastData?.currentStock?.toLocaleString() || "0"}
                            {` `}
                            {product?.productStockDetails?.unitOfMeasurement || ""}
                        </span>
                    </TableBodyItem>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <TableBodyItem>
                        <div className="flex items-center space-x-1.5">
                            {Boolean(product?.forcastData?.forecastedNeeds) && (
                                <div>
                                    <a id={`forecast-needs-${idx}`}>
                                        <span className="material-symbols-outlined text-[14px] text-r-50">
                                            warning
                                        </span>
                                    </a>

                                    <Tooltip
                                        anchorSelect={`#forecast-needs-${idx}`}
                                        place={"top"}
                                        content={
                                            Number(product?.productStockDetails?.stockLevel) < 1
                                                ? "Product is out of stock"
                                                : "Stock for this product may soon run out."
                                        }
                                        style={{ width: "180px", zIndex: 9999 }}
                                    />
                                </div>
                            )}
                            {product?.forcastData?.forecastedNeeds ? (
                                <span className="text-[#FF3D1D]">
                                    {millify(product?.forcastData?.forecastedNeeds, {
                                        precision: 2,
                                    })}{" "}
                                    {product?.productStockDetails?.unitOfMeasurement || ""}
                                </span>
                            ) : !product?.productStockDetails?.stockLevel ||
                              product?.productStockDetails?.stockLevel === 0 ? (
                                <span className="text-[#FF3D1D]">Stockout</span>
                            ) : (
                                <span className="text-[#56B648]">Sufficient</span>
                            )}
                        </div>
                    </TableBodyItem>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <TableBodyItem>
                        <span>{millify(product?.onOrder, { precision: 2 })}</span>
                    </TableBodyItem>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <TableBodyItem>
                        <span>
                            {(() => {
                                const reorderDate = calculateStockoutDate(
                                    product?.forcastData?.stockoutDate,
                                    product?.leadTime as { duration: string; value: number }
                                );
                                const differenceInDaysInReorderDate = reorderDate?.diff(
                                    today,
                                    "days"
                                );
                                return reorderDate ? (
                                    <div className="flex flex-col gap-1">
                                        <span
                                            className={clsx(
                                                differenceInDaysInReorderDate > 0
                                                    ? "text-slate-500"
                                                    : "text-[#E4281D]"
                                            )}
                                        >
                                            {differenceInDaysInReorderDate === 0
                                                ? "Today"
                                                : formatMoney().format(
                                                      differenceInDaysInReorderDate
                                                  ) + " days"}
                                        </span>
                                        <span>{reorderDate.format("MMM DD, YYYY")}</span>
                                    </div>
                                ) : (
                                    "Not Available"
                                );
                            })()}
                        </span>
                    </TableBodyItem>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <TableBodyItem className="truncate whitespace-nowrap">
                        <div>
                            <span className="text-sm font-rocGroteskMedium">
                                {!product?.forcastData?.incidentType ? (
                                    "Not Available"
                                ) : product?.forcastData?.incidentType?.toLowerCase() !==
                                  "stockout" ? (
                                    <span className=""> Overstocked </span>
                                ) : //  : product?.forcastData?.incidentType?.toLowerCase() ===
                                //       "stockout" ||
                                differenceInDays > 0 && differenceInDays <= 7 ? (
                                    <>
                                        <span className="text-[#E4281D] block"> Immediately </span>
                                        <span className={` text-slate-500 block`}>
                                            {moment().format("MMM DD, YYYY")}
                                        </span>
                                    </>
                                ) : differenceInDays < 0 ? (
                                    "Not Available"
                                ) : (
                                    <>
                                        <span
                                            className={`block ${
                                                differenceInDays > 7
                                                    ? "text-[#56B648]"
                                                    : "text-[#FF3D1D]"
                                            }`}
                                        >
                                            {differenceInDays === 0
                                                ? "Today"
                                                : formatMoney().format(differenceInDays) + " days"}
                                        </span>
                                        <span className={`block text-slate-500`}>
                                            {moment(product?.forcastData?.stockoutDate).format(
                                                "MMM DD, YYYY"
                                            )}
                                        </span>
                                    </>
                                )}
                            </span>
                        </div>
                    </TableBodyItem>
                ),
                widthClass: "!border-slate-200",
            },
        ];
    });

    const displayedChannel = () => {
        if (channel) {
            const normalized = normalizeSalesChannelName(channel);
            return normalized || channel;
        }
        return "All Channels";
    };

    const isAllChannelsChecked = salesChannel.every((_channel) => channel?.includes(_channel));

    const handleCheckAllChannels = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const allSalesChannels = salesChannel?.join(",");

        if (isAllChannelsChecked) {
            handleClearChannel();
        } else {
            handleChannelChange(allSalesChannels);
        }
    };

    const handleChangeChannelFilter = (name, value) => {
        const handlePrev = (prev) => {
            if (!prev) return value;
            if (prev?.includes(value)) {
                const newChannel = prev?.split(",")?.filter((item) => item !== value);
                return newChannel.join(",");
            }
            const newChannel = [...prev?.split(","), value];
            return newChannel.join(",");
        };

        const newValue = handlePrev(channel);
        handleChannelChange(newValue);
    };

    return (
        <Fragment>
            <div className="p-[24px]">
                <p className="text-sm font-rocGroteskMedium text-slate-500">
                    Only admins can add and remove products in this Space.
                </p>
                <div className="flex gap-2 items-center mt-[16px]">
                    <SearchTextField
                        name={"search"}
                        value={search}
                        type={"text"}
                        inputPlaceholder="Search products"
                        onChange={handleSearch}
                        onInput={handleDebouncedChange}
                        containerClassname="!min-w-[200px] !border-slate-200"
                        inputContainerClassname="!border-slate-200"
                    />

                    <div className="flex items-center gap-2">
                        <Dropdown
                            handleChange={(name, value) => {
                                setForecastNeeds(value);
                            }}
                            dropdown={
                                <div className="flex items-center justify-between whitespace-nowrap h-[40px]  py-2 pl-3 pr-2 space-x-1 rounded-[5px] border border-slate-200">
                                    <div className="flex items-center space-x-2">
                                        <span
                                            className={`text-g-75 font-rocGroteskMedium text-[13px]`}
                                        >
                                            {forecastNeeds || "Forecast Needs"}
                                        </span>
                                        {Boolean(forecastNeeds) && (
                                            <i
                                                onClick={(e) => {
                                                    e?.stopPropagation();
                                                    setForecastNeeds("");
                                                    updatedPage !== 1 && setUpdatedPage(1);
                                                }}
                                                className="ri-close-circle-fill text-sm text-slate-500"
                                            ></i>
                                        )}
                                    </div>
                                    <i className="ri-arrow-down-s-line text-g-75"></i>
                                </div>
                            }
                            wholeContainerClass="!w-fit max-sm:!w-full"
                            dropdownContainerClasses="shadow-cardShadow-4 !w-full !max-w-full min-w-full border border-slate-100 rounded !max-h-fit !pt-0 !z-[1400]"
                            dropdownOptions={[
                                {
                                    label: "Stockout",
                                    value: "stockout",
                                },
                                {
                                    label: "Sufficient",
                                    value: "sufficient",
                                },
                            ]}
                        />

                        <Dropdown
                            name="Channel Filter"
                            value={""}
                            dropdownTriggerClassName=" "
                            handleChange={handleChangeChannelFilter}
                            isLoading={salesChannelLoading}
                            dropdownOptions={[
                                {
                                    label: (
                                        <MenuItem onClick={handleCheckAllChannels}>
                                            <Checkbox
                                                checked={isAllChannelsChecked}
                                                label={
                                                    <div className="flex items-center gap-2 space-x-2">
                                                        <i className="ri-box-3-line text-xl"></i>
                                                        All Channels
                                                    </div>
                                                }
                                            />
                                        </MenuItem>
                                    ),
                                    value: null,
                                },
                                ...salesChannel?.map((_channel, idx) => {
                                    const isChecked = channel?.includes(_channel);

                                    return {
                                        label: (
                                            <MenuItem key={idx}>
                                                <Checkbox
                                                    checked={isChecked}
                                                    label={
                                                        <div className="flex items-center gap-2 space-x-2">
                                                            {getSalesChannelIcon(_channel)}
                                                            {normalizeSalesChannelName(_channel)}
                                                        </div>
                                                    }
                                                />
                                            </MenuItem>
                                        ),
                                        value: _channel,
                                    };
                                }),
                            ]}
                            dropdown={
                                <div className="flex items-center justify-between whitespace-nowrap h-[40px]  py-2 pl-3 pr-2 space-x-1 rounded-[5px] border border-slate-200">
                                    <div className="flex items-center space-x-2">
                                        <span className="text-[13px] whitespace-nowrap font-rocGroteskMedium capitalize">
                                            {displayedChannel()}
                                        </span>
                                        {Boolean(channel) && (
                                            <i
                                                onClick={(e) => {
                                                    e?.stopPropagation();

                                                    handleClearChannel();
                                                }}
                                                className="ri-close-circle-fill text-sm text-slate-500"
                                            ></i>
                                        )}
                                    </div>
                                </div>
                            }
                            wholeContainerClass="!w-fit max-sm:!w-full"
                            dropdownContainerClasses={`shadow-cardShadow-4 !w-full !max-w-full min-w-full border border-slate-100 rounded !max-h-fit !right-0 !pt-0 !z-[1400]`}
                        />
                    </div>
                </div>

                <div className="mt-[16px] w-full overflow-x-auto">
                    <CustomTable
                        tableBody={tableBody}
                        tableHeader={tableHeader}
                        isCellBordered={true}
                        stickyHeader={true}
                        isScrollable={true}
                        headerContainerClass="!bg-slate-50 !border-slate-200"
                        headerItemClass="font-rocGroteskMedium "
                        tableClass="!border-slate-200 scrollbar-hidden"
                        scrollContentId="table-container-one"
                        isLoading={productListLoading && data.length < 1}
                        renderEmptyTable={() => (
                            <SpaceEmptyContent
                                title="No pinned SKUs yet"
                                icon="ri-instance-line"
                                description="SKUs you pin will appear here for quick access in this space. Pin important products so your team can easily track and reference them."
                                action={() => {}}
                                actionText="Pin SKU"
                            />
                        )}
                    />

                    {productListLoading && (
                        <div className="flex justify-center my-4">
                            <Loader color="gm-25" size={4} />
                        </div>
                    )}

                    {pagination?.currentPage === pagination?.noOfPages && (
                        <div className="flex my-4 justify-center text-slate-500 text-sm font-rocGroteskMedium">
                            End of list
                        </div>
                    )}
                </div>
            </div>
        </Fragment>
    );
}
